import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Optional, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, forkJoin, Subscription } from 'rxjs';
import { GenerateCocPopupComponent, IGenerateCocPopup } from 'src/app/common-components/generate-coc-popup/generate-coc-popup.component';
import { LabRequisitionDetailsPopupComponent } from 'src/app/common-components/lab-requisition-details-popup/lab-requisition-details-popup.component';
import { LabResultImportDialogComponent } from 'src/app/modals/lab-result-import-dialog/lab-result-import-dialog.component';
import { LabResultImportDialogData } from 'src/app/modals/lab-result-import-dialog/lab-result-import.model';
import { LabRequisitionStatus } from 'src/app/enums/labRequisitionStatus.enum';
import { SecurityProfileEnum } from 'src/app/enums/securityProfile.enum';
import { WidgetConfig } from 'src/app/models/dashboard';
import { LabRequisition } from 'src/app/models/labRequisition.model';
import { Lookup } from 'src/app/models/lookup.model';
import { AuthService } from 'src/app/services';
import { CommonService } from 'src/app/services/common.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { LabRequisitionService } from 'src/app/services/lab-requisition.service';
import { LabService } from 'src/app/services/lab.service';
import { OrganizationLevelService } from 'src/app/services/organizationLevel.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslateService } from 'src/app/services/translate.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { sampleRouteConfig } from 'src/app/shared/constants/sampleRouteConfig.constants';
import { DeleteDialog, IDeleteDialogData } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { FlyoutContainerComponent } from 'src/app/shared/flyout/flyout-container/flyout-container.component';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { WIDGET_CONFIG } from 'src/app/shared/grid/baseGrid.component';
import { openFileInNewTab } from 'src/app/shared/utils/file-link.util';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';

@Component({
  standalone: false,
  selector: 'app-lab-requisition-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss']
})
export class LabRequisitionListComponent extends CoreGridComponent<LabRequisition> implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

  organizationLevels: Lookup[] = [];
  labs: Lookup[] = [];
  private routeParamsSubscription: Subscription;

  constructor(
    protected gridService: LabRequisitionService,
    protected toastService: ToastService,
    protected dialog: MatDialog,
    protected commonService: CommonService,
    authService: AuthService,
    gridViewService: GridViewService,
    gridSettingsService: GridSettingsService,
    private organizationLevelService: OrganizationLevelService,
    private labService: LabService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    @Optional() @Inject(WIDGET_CONFIG) widgetConfig?: WidgetConfig
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService, widgetConfig);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadLookupData();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
    this.setupFlyoutInterceptor();
    this.subscribeToQueryParams();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.routeParamsSubscription?.unsubscribe();
  }

  protected get objectId(): string {
    return RolePermissionObjects.LabRequisition;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  get hasOrgFilter(): boolean {
    return true;
  }

  override initializeColumns(): void {
    this.additionalActions = [
      {
        name: 'Import Results',
        icon: 'fa fa-file-import',
        action: (labRequisition: LabRequisition) => this.openImportResults(labRequisition)
      }
    ];

    this.columnConfigs = [
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.labRequisitionNumber),
        displayName: 'Lab Requisition Number',
        isSortable: true,
        cssClass: () => 'cursor-pointer',
        onClick: (labRequisition: LabRequisition) => this.openLabRequisitionDetailsPopup(labRequisition.id)
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToCity),
        displayName: 'Report To City'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoicePoNumber),
        displayName: 'PO'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.accountNumber),
        displayName: 'Account'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.requisitionStatus),
        displayName: 'Status',
        cellTemplate: (labRequisition) => LabRequisitionStatus[labRequisition.requisitionStatus] ? this.translateService.getDynamicTranslation(LabRequisitionStatus[labRequisition.requisitionStatus]) : ' - ',
        enumFilterMap: this.enumToValueNameArray(LabRequisitionStatus)
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.emailEdd),
        displayName: 'Email EDD'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoiceTo),
        displayName: 'Invoice To'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoiceCompanyName),
        displayName: 'Invoice Company Name'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoiceComments),
        displayName: 'Invoice Comments'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoiceAddress1),
        displayName: 'Invoice Address 1'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoiceAddress2),
        displayName: 'Invoice Address 2'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoicePhoneNumber),
        displayName: 'Invoice Phone Number'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoiceEmail),
        displayName: 'Invoice Email'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.invoicePaymentInfo),
        displayName: 'Invoice Payment Info'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportTo),
        displayName: 'Report To'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToCompanyName),
        displayName: 'Report To Company Name'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToAddress1),
        displayName: 'Report To Address 1'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToAddress2),
        displayName: 'Report To Address 2'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToState),
        displayName: 'Report To State'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToZip),
        displayName: 'Report To Zip'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToPhoneNumber),
        displayName: 'Report To Phone Number'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToCellNumber),
        displayName: 'Report To Cell Number'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToEmail),
        displayName: 'Report To Email'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.reportToComments),
        displayName: 'Report To Comments'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.paymentTerms),
        displayName: 'Payment Terms'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.labId),
        displayName: 'Lab',
        cellTemplate: (e) => this.labs?.find(lab => lab.id === e.labId)?.name || '-'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.generalComments),
        displayName: 'General Comments'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.industryProcessInterferenceDescription),
        displayName: 'Industry Process Interference Description'
      }),
      new ColumnConfig<LabRequisition>({
        name: nameOf<LabRequisition>(x => x.organizationLevelId),
        displayName: 'Visibility',
        cellTemplate: (e) => this.organizationLevels?.find(org => org.id === e.organizationLevelId)?.name || '-'
      }),
      { name: 'action', displayName: 'Actions' }
    ];
  }

  override initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  override initializeTabs() {
    return sampleRouteConfig;
  }

  override initializeProperties(): void {
    this.addEntityTitle = 'Lab Requisitions';
    this.searchPlaceholder = 'Search Lab Requisitions';
  }

  override getOverviewData(): Observable<void> {
    return of(undefined);
  }

  override objectFilterMap(propertyName: string): any | undefined {
    const filterMap = {
      [nameOf<LabRequisition>(x => x.organizationLevelId)]: this.organizationLevelService,
      [nameOf<LabRequisition>(x => x.labId)]: this.labService
    };
    return filterMap[propertyName];
  }

  override openEntityDialog(labRequisitionId?: string): void {
    const labRequisitionModel: IGenerateCocPopup = { id: labRequisitionId };
    this.openFlyoutInternal(GenerateCocPopupComponent, labRequisitionModel, '80vw', true);
  }

  override openEntityDeleteDialog(labRequisition: LabRequisition): void {
    const linkedSampleCount = labRequisition.sampleIds?.length ?? 0;
    const confirmationMessage = linkedSampleCount > 0
      ? this.translateService.getDynamicTranslation(
          'This Lab Requisition has {{count}} linked sample(s). Deleting it will unlink all of them first. Are you sure you want to continue?',
          { count: linkedSampleCount }
        )
      : undefined;

    const dialogRef = this.dialog.open(DeleteDialog, {
      data: {
        id: labRequisition.id,
        formDeletes: true,
        service: this.gridService,
        title: 'Lab Requisition',
        confirmationMessage
      } as IDeleteDialogData,
      width: '440px',
      disableClose: true,
      ariaLabel: 'Lab Requisition',
      panelClass: 'delete-confirmation-dialog'
    });
    this.handleDialogClose(dialogRef);
  }

  protected override onFlyoutClose(): void {
    this.router.navigate(['/dashboard/samples/lab-requisition']);
  }

  openPDF(labRequisition: LabRequisition): void {
    this.gridService.getById(labRequisition.id).subscribe({
      next: labRequisition => {
        const link = labRequisition.fileAttachments?.[0]?.url;
        if (!openFileInNewTab(link)) {
          this.toastService.displayErrorMessage("PDF not found", true);
        }
      },
      error: (error) => {
        this.toastService.showError(error);
      }
    });
  }

  openImportResults(labRequisition: LabRequisition): void {
    // Only Admin/SuperAdmin may import results — mirrors the commit authorization at the BE and the
    // guard added at the details-popup call site. Blocks exposing the full mapping/analysis UI to
    // unauthorized users before any authorization is asserted.
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser || ![SecurityProfileEnum.Admin, SecurityProfileEnum.SuperAdmin].includes(currentUser.userType)) {
      this.toastService.displayErrorMessage('You do not have permission to import lab results', true);
      return;
    }
    const data: LabResultImportDialogData = {
      labRequisitionId: labRequisition.id,
      labRequisitionNumber: labRequisition.labRequisitionNumber
    };
    const dialogRef = this.dialog.open(LabResultImportDialogComponent, {
      data,
      width: '900px',
      maxHeight: '90vh',
      disableClose: true
    });
    dialogRef.afterClosed().subscribe((imported: boolean) => {
      if (imported) {
        this.loadPage();
      }
    });
  }

  openLabRequisitionDetailsPopup(labRequisitionId: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { id: labRequisitionId },
      queryParamsHandling: 'merge'
    });

    this.openFlyoutInternal(LabRequisitionDetailsPopupComponent, labRequisitionId, '85vw', true);
  }

  private loadLookupData(): void {
    forkJoin({
      organizationLevels: this.organizationLevelService.getAll(),
      labs: this.labService.getAll()
    }).subscribe(({ organizationLevels, labs }) => {
      this.organizationLevels = organizationLevels;
      this.labs = labs;
    });
  }

  private setupFlyoutInterceptor(): void {
    if (!this.flyout) return;

    const originalClose = this.flyout.close.bind(this.flyout);
    this.flyout.close = () => {
      originalClose();
      if (this.isFlyoutOpen) {
        this.onFlyoutClose();
      }
    };
  }

  private subscribeToQueryParams(): void {
    this.routeParamsSubscription = this.route.queryParams.subscribe(params => {
      const labRequisitionId = params['id'];

      if (labRequisitionId) {
        setTimeout(() => {
          if (!this.isFlyoutOpen) {
            this.openLabRequisitionDetailsPopup(labRequisitionId);
          }
        }, 0);
      } else if (this.isFlyoutOpen) {
        this.closeFlyoutInternal();
      }
    });
  }

}
